import { forwardRef, useEffect, useId, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import * as Dialog from '@radix-ui/react-dialog';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { cn } from '@/styles/tailwind-utils';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';

import type { ClientListItem, ClientReferralSource, ClientSex, CreateClientRequest } from '../types';
import { parseCnp } from '../utils/cnp';

import { DateOfBirthPicker } from './DateOfBirthPicker';



// Country is fixed to Romania across the app — never shown, always sent as "RO".
const COUNTRY_RO = 'RO';

// The Bucharest municipality acts as its own county; selecting it reveals the
// sector field and auto-fills the city.
const BUCHAREST = 'București';

// All 41 Romanian counties (județe) plus the Bucharest municipality.
const COUNTIES: string[] = [
  'Alba', 'Arad', 'Argeș', 'Bacău', 'Bihor', 'Bistrița-Năsăud', 'Botoșani',
  'Brașov', 'Brăila', BUCHAREST, 'Buzău', 'Caraș-Severin', 'Călărași', 'Cluj',
  'Constanța', 'Covasna', 'Dâmbovița', 'Dolj', 'Galați', 'Giurgiu', 'Gorj',
  'Harghita', 'Hunedoara', 'Ialomița', 'Iași', 'Ilfov', 'Maramureș', 'Mehedinți',
  'Mureș', 'Neamț', 'Olt', 'Prahova', 'Satu Mare', 'Sălaj', 'Sibiu', 'Suceava',
  'Teleorman', 'Timiș', 'Tulcea', 'Vaslui', 'Vâlcea', 'Vrancea',
];

const SECTORS: string[] = [
  'Sector 1', 'Sector 2', 'Sector 3', 'Sector 4', 'Sector 5', 'Sector 6',
];

// ─── SelectField ─────────────────────────────────────────────────────────────

interface SelectFieldProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  children: React.ReactNode;
}

const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, error, id: idProp, children, className, ...props },
  ref,
) {
  const generatedId = useId();
  const id = idProp ?? generatedId;

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={id} className="text-sm font-medium text-foreground">
          {label}
        </label>
      )}
      <select
        ref={ref}
        id={id}
        className={cn(
          'h-10 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm text-foreground',
          'focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1',
          'disabled:cursor-not-allowed disabled:opacity-50',
          error && 'border-destructive focus:ring-destructive',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
});

// ─── Constants ────────────────────────────────────────────────────────────────

const SEX_OPTIONS: ClientSex[] = ['FEMALE', 'MALE', 'NOT_STATED'];
const REFERRAL_SOURCES: ClientReferralSource[] = ['WALK_IN', 'REFERRAL', 'ONLINE', 'OTHER'];

// ─── Component ────────────────────────────────────────────────────────────────

interface ClientDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** null → add mode, non-null → edit mode with pre-filled values */
  client: ClientListItem | null;
  onSubmit: (data: CreateClientRequest) => void;
  isSubmitting: boolean;
  errorMessage: string | null;
}

export function ClientDrawer({
  isOpen,
  onClose,
  client,
  onSubmit,
  isSubmitting,
  errorMessage,
}: ClientDrawerProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const isEditMode = client !== null;

  const [dirtyWarningVisible, setDirtyWarningVisible] = useState(false);
  const [dirtyWarningKey, setDirtyWarningKey] = useState(0);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const schema = useMemo(
    () =>
      z.object({
        firstName: z.string().min(1, t('clients.addClientModal.firstNameRequired')),
        lastName: z.string().min(1, t('clients.addClientModal.lastNameRequired')),
        email: z
          .string()
          .min(1, t('clients.addClientModal.emailRequired'))
          .email(t('clients.addClientModal.emailInvalid')),
        phone: z
          .string()
          .min(1, t('clients.addClientModal.phoneRequired'))
          .regex(/^\+[1-9]\d{1,14}$/, t('clients.addClientModal.phoneInvalid')),
        dateOfBirth: z
          .string()
          .min(1, t('clients.addClientModal.dateOfBirthRequired'))
          .refine((v) => v <= today, t('clients.addClientModal.dateOfBirthFuture')),
        gender: z.string(),
        cnp: z
          .string()
          .refine((v) => v === '' || /^\d{13}$/.test(v), t('clients.addClientModal.cnpInvalid')),
        address: z
          .object({
            county: z.string(),
            city: z.string(),
            sector: z.string(),
            street: z.string(),
            streetNumber: z.string(),
            building: z.string(),
            entrance: z.string(),
            apartment: z.string(),
            postalCode: z.string(),
          })
          .superRefine((addr, ctx) => {
            // Per the API contract, sector is required for Bucharest addresses.
            if (addr.county === BUCHAREST && !addr.sector) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['sector'],
                message: t('clients.profileDrawer.sectorRequired'),
              });
            }
          }),
        referralSource: z.string(),
      }),
    [t, today],
  );

  type FormValues = z.infer<typeof schema>;

  function buildDefaults(c: ClientListItem | null): FormValues {
    return {
      firstName: c?.firstName ?? '',
      lastName: c?.lastName ?? '',
      email: c?.email ?? '',
      phone: c?.phone ?? '',
      dateOfBirth: c?.dateOfBirth ?? '',
      gender: '',
      cnp: '',
      address: {
        county: '',
        city: '',
        sector: '',
        street: '',
        streetNumber: '',
        building: '',
        entrance: '',
        apartment: '',
        postalCode: '',
      },
      referralSource: '',
    };
  }

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: buildDefaults(client),
  });

  useEffect(() => {
    if (isOpen) {
      reset(buildDefaults(client));
      setDirtyWarningVisible(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, client, reset]);

  // A complete CNP encodes the client's birth date and sex — derive both and
  // pre-fill the matching fields so staff don't re-enter them.
  const cnpValue = watch('cnp');
  useEffect(() => {
    const parsed = parseCnp(cnpValue);
    if (!parsed) return;
    if (getValues('dateOfBirth') !== parsed.dateOfBirth) {
      setValue('dateOfBirth', parsed.dateOfBirth, { shouldValidate: true, shouldDirty: true });
    }
    if (getValues('gender') !== parsed.sex) {
      setValue('gender', parsed.sex, { shouldValidate: true, shouldDirty: true });
    }
  }, [cnpValue, getValues, setValue]);

  // Selecting Bucharest as the county auto-fills the city and reveals the
  // sector field; switching away clears any previously chosen sector.
  const selectedCounty = watch('address.county');
  const isBucharest = selectedCounty === BUCHAREST;

  useEffect(() => {
    if (isBucharest) {
      if (getValues('address.city') !== BUCHAREST) {
        setValue('address.city', BUCHAREST, { shouldDirty: true });
      }
    } else if (getValues('address.sector')) {
      setValue('address.sector', '', { shouldDirty: true });
    }
  }, [isBucharest, getValues, setValue]);

  const emptyToNull = (v: string): string | null => {
    const trimmed = v.trim();
    return trimmed === '' ? null : trimmed;
  };

  function onFormSubmit(values: FormValues) {
    onSubmit({
      firstName: values.firstName,
      lastName: values.lastName,
      dateOfBirth: values.dateOfBirth,
      email: values.email,
      phone: values.phone,
      ...(values.gender && { sex: values.gender as ClientSex }),
      ...(values.cnp.trim() && { identifiers: { cnp: values.cnp.trim() } }),
      address: {
        county: emptyToNull(values.address.county),
        city: emptyToNull(values.address.city),
        // Sector only applies to Bucharest; null everywhere else.
        sector: isBucharest ? emptyToNull(values.address.sector) : null,
        street: emptyToNull(values.address.street),
        streetNumber: emptyToNull(values.address.streetNumber),
        building: emptyToNull(values.address.building),
        entrance: emptyToNull(values.address.entrance),
        apartment: emptyToNull(values.address.apartment),
        postalCode: emptyToNull(values.address.postalCode),
        // Country is fixed to Romania.
        country: COUNTRY_RO,
      },
      ...(values.referralSource && { referralSource: values.referralSource as ClientReferralSource }),
    });
  }

  function handleInteractOutside(e: Event) {
    if (isDirty) {
      e.preventDefault();
      setDirtyWarningVisible(true);
      setDirtyWarningKey((k) => k + 1);
    }
  }

  const title = isEditMode
    ? t('clients.clientDrawer.editTitle')
    : t('clients.addClientModal.title');

  const subtitle = isEditMode
    ? t('clients.clientDrawer.editSubtitle', {
        name: `${client.firstName} ${client.lastName}`,
      })
    : t('clients.addClientDrawer.subtitle');

  const submitLabel = isEditMode
    ? t('clients.clientDrawer.editSubmit')
    : t('clients.addClientModal.submit');

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        {/* Backdrop */}
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />

        {/* Drawer panel */}
        <Dialog.Content
          aria-modal
          aria-labelledby={titleId}
          onPointerDownOutside={handleInteractOutside}
          onInteractOutside={handleInteractOutside}
          className={cn(
            'fixed right-0 top-0 z-50 flex h-full w-full flex-col bg-surface shadow-2xl',
            'sm:w-[48%] sm:min-w-[480px] sm:max-w-[680px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
            'duration-300 ease-in-out',
          )}
        >
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between border-b border-border px-6 py-4">
            <div>
              <Dialog.Title
                id={titleId}
                className="text-base font-semibold text-foreground"
              >
                {title}
              </Dialog.Title>
              <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
            </div>
            <Dialog.Close asChild>
              <button
                aria-label={t('layout.closeDialog')}
                className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <path d="M2 2l12 12M14 2L2 14" />
                </svg>
              </button>
            </Dialog.Close>
          </div>

          {/* Unsaved-changes warning — flashes twice when user tries to dismiss */}
          {dirtyWarningVisible && (
            <div
              key={dirtyWarningKey}
              role="status"
              className="shrink-0 border-b border-amber-200/80 bg-amber-50 px-6 py-2.5"
              style={{ animation: 'drawer-flash 0.4s ease 2' }}
            >
              <style>{`@keyframes drawer-flash{0%,100%{opacity:1}50%{opacity:0.1}}`}</style>
              <p className="text-sm text-amber-800">
                <span className="font-semibold">{t('clients.addClientDrawer.unsavedTitle')}</span>
                {' '}{t('clients.addClientDrawer.unsavedDesc')}
              </p>
            </div>
          )}

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {errorMessage && (
              <div role="alert" className="mb-5 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {errorMessage}
              </div>
            )}

            <form
              id="client-drawer-form"
              onSubmit={handleSubmit(onFormSubmit)}
              noValidate
              className="space-y-5"
            >
              {/* Section: Basic info */}
              <fieldset className="space-y-4">
                <legend className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('clients.addClientDrawer.sectionBasicInfo')}
                </legend>

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label={t('clients.addClientModal.firstNameLabel')}
                    error={errors.firstName?.message}
                    autoFocus
                    {...register('firstName')}
                  />
                  <Input
                    label={t('clients.addClientModal.lastNameLabel')}
                    error={errors.lastName?.message}
                    {...register('lastName')}
                  />
                </div>

                <Input
                  label={t('clients.addClientModal.cnpLabel')}
                  inputMode="numeric"
                  placeholder={t('clients.addClientModal.cnpPlaceholder')}
                  error={errors.cnp?.message}
                  {...register('cnp')}
                />

                <div className="grid grid-cols-2 gap-3">
                  <Controller
                    control={control}
                    name="dateOfBirth"
                    render={({ field, fieldState }) => (
                      <DateOfBirthPicker
                        label={t('clients.addClientModal.dateOfBirthLabel')}
                        value={field.value}
                        onChange={field.onChange}
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                  <SelectField
                    label={t('clients.addClientModal.genderLabel')}
                    error={errors.gender?.message}
                    {...register('gender')}
                  >
                    <option value="">{t('clients.addClientModal.genderPlaceholder')}</option>
                    {SEX_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {t(`clients.addClientModal.gender.${s}`)}
                      </option>
                    ))}
                  </SelectField>
                </div>
              </fieldset>

              {/* Section: Contact */}
              <fieldset className="space-y-4">
                <legend className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('clients.addClientDrawer.sectionContact')}
                </legend>

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label={t('clients.addClientModal.emailLabel')}
                    type="email"
                    error={errors.email?.message}
                    {...register('email')}
                  />
                  <Input
                    label={t('clients.addClientModal.phoneLabel')}
                    type="tel"
                    placeholder={t('clients.addClientModal.phonePlaceholder')}
                    error={errors.phone?.message}
                    {...register('phone')}
                  />
                </div>
              </fieldset>

              {/* Section: Address */}
              <fieldset className="space-y-3">
                <legend className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('clients.profileDrawer.addressLegend')}
                </legend>

                {/* County + City */}
                <div className="grid grid-cols-2 gap-3">
                  <SelectField
                    label={t('clients.profileDrawer.countyLabel')}
                    {...register('address.county')}
                  >
                    <option value="">{t('clients.profileDrawer.countyPlaceholder')}</option>
                    {COUNTIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </SelectField>
                  <Input label={t('clients.profileDrawer.cityLabel')} {...register('address.city')} />
                </div>

                {/* Street */}
                <Input label={t('clients.profileDrawer.streetLabel')} {...register('address.street')} />

                {/* Number + Building + Entrance + Apartment */}
                <div className="grid grid-cols-4 gap-3">
                  <Input label={t('clients.profileDrawer.streetNumberLabel')} {...register('address.streetNumber')} />
                  <Input label={t('clients.profileDrawer.buildingLabel')} {...register('address.building')} />
                  <Input label={t('clients.profileDrawer.entranceLabel')} {...register('address.entrance')} />
                  <Input label={t('clients.profileDrawer.apartmentLabel')} {...register('address.apartment')} />
                </div>

                {/* Postal code + Sector (Bucharest only) */}
                <div className="grid grid-cols-2 gap-3">
                  <Input label={t('clients.profileDrawer.postalCodeLabel')} {...register('address.postalCode')} />
                  {isBucharest && (
                    <SelectField
                      label={t('clients.profileDrawer.sectorLabel')}
                      error={errors.address?.sector?.message}
                      {...register('address.sector')}
                    >
                      <option value="">{t('clients.profileDrawer.sectorPlaceholder')}</option>
                      {SECTORS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </SelectField>
                  )}
                </div>
              </fieldset>

              {/* Section: Additional */}
              <fieldset className="space-y-4">
                <legend className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t('clients.addClientDrawer.sectionAdditional')}
                </legend>

                <SelectField
                  label={t('clients.addClientModal.referralSourceLabel')}
                  error={errors.referralSource?.message}
                  {...register('referralSource')}
                >
                  <option value="">{t('clients.addClientModal.referralSourcePlaceholder')}</option>
                  {REFERRAL_SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {t(`clients.addClientModal.referralSource.${s}`)}
                    </option>
                  ))}
                </SelectField>
              </fieldset>
            </form>
          </div>

          {/* Footer */}
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border px-6 py-4">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
              {t('clients.addClientDrawer.discard')}
            </Button>
            <Button type="submit" form="client-drawer-form" isLoading={isSubmitting}>
              {submitLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
